import { useEffect, useRef, useState } from "react";

import XMarkIcon from "@heroicons/react/20/solid/XMarkIcon";
import { useTransition, animated, useSpringRef } from "@react-spring/web";
import { useRouter } from "next/router";
import qs from "qs";
import { FormProvider, useForm } from "react-hook-form";

import ErrorBoundary from "components/errorBoundary/ErrorBoundary";
import { useGetProduct, useSetCustomisation } from "queries/product-queries";
import { SSR_CONFIGURATION_ERROR } from "utils/error-utils";
import { CANVAS_FALLBACK_CONTENT } from "utils/fallback-content";
import { useGetDefaultValuesFromFormDocuments } from "utils/form-utils";
import { useInterpolate } from "utils/prismic-utils";
import { useFindByKey } from "utils/schema-utils";
import { useGetPathnameForDestination } from "utils/url-utils";

import styles from "./CreationFlow.module.scss";
import CreationFlowStep from "./creationFlowStep/CreationFlowStep";

export default function CreationFlow() {
  const router = useRouter();
  const transRef = useSpringRef();

  const { data: product } = useGetProduct();
  const { mutate: setCustomisation } = useSetCustomisation();

  const getPathnameForDestination = useGetPathnameForDestination();
  const findByKey = useFindByKey();
  const interpolate = useInterpolate();

  if (!product) {
    throw SSR_CONFIGURATION_ERROR;
  }

  // Website product page URL for use on cancel/back
  const productPageUrl = getPathnameForDestination({
    destination: "website",
    path: "/personalized-products/" + router.query.solidus_slug,
  });

  // Only show steps that feature in the schema
  const creationFlowData = product.content.product.creation_flow_steps.filter(
    // The name of this function is the same of a proprietaty testing library
    // function so we we need to disable the eslint rule
    // eslint-disable-next-line testing-library/await-async-query
    ({ muse_schema_key }) => muse_schema_key && !!findByKey(muse_schema_key)
  );

  const methods = useForm({
    defaultValues: useGetDefaultValuesFromFormDocuments(
      creationFlowData.map((c) => c.form as any)
    ),
  });

  const [currentStep, setCurrentStep] = useState(0);

  const previousStep = useRef(-1);

  const transitions = useTransition(currentStep, {
    ref: transRef,
    keys: null,
    immediate: previousStep.current === -1,
    from: () => ({
      opacity: 0,
      transform: `translateX(${
        previousStep.current < currentStep ? 40 : -40
      }%)`,
    }),
    enter: () => ({
      opacity: 1,
      transform: `translateX(0%)`,
    }),
    leave: () => ({
      opacity: 0,
      transform: `translateX(${
        previousStep.current < currentStep ? -40 : 40
      }%)`,
    }),
    config: {
      tension: 220,
      friction: 14,
      clamp: true,
    },
    onRest: () => {
      previousStep.current = currentStep;
    },
  });

  useEffect(() => {
    transRef.start();
  }, [currentStep, transRef]);

  async function onStepSuccess(data: qs.ParsedQs) {
    // Save new customisation to local storage
    setCustomisation({ data });

    if (currentStep === creationFlowData.length - 1) {
      router.push(getPathnameForDestination({ path: "/create/preview" }));
    } else {
      setCurrentStep(currentStep + 1);
    }
  }

  function onBackClicked() {
    if (currentStep === 0) {
      window.location.href = productPageUrl;
    } else {
      setCurrentStep(currentStep - 1);
    }
  }

  if (!creationFlowData[currentStep]) {
    return null;
  }

  return (
    <div className={styles.creationFlow}>
      <header className={styles.header}>
        <h1 className={styles.headerTitle}>
          {product.content.product.muse_product_name}
        </h1>

        <a
          className={styles.closeButton}
          href={productPageUrl}
          aria-label={interpolate(
            product.content.canvas.back_product_page_link ??
              CANVAS_FALLBACK_CONTENT.back_product_page_link
          )}
        >
          <XMarkIcon />
        </a>
      </header>

      <FormProvider {...methods}>
        <main className={styles.step}>
          {transitions((style, i) => (
            <animated.div className={styles.stepInner} style={style}>
              <ErrorBoundary>
                <CreationFlowStep
                  currentStep={i + 1}
                  data={creationFlowData[i]}
                  numberOfSteps={creationFlowData.length}
                  onBack={onBackClicked}
                  onSuccess={onStepSuccess}
                />
              </ErrorBoundary>
            </animated.div>
          ))}
        </main>
      </FormProvider>

      {/* eslint-disable-next-line react/no-unknown-property */}
      <style global jsx>{`
        body {
          overflow-x: hidden;
        }
      `}</style>
    </div>
  );
}
